import React, { FC, useEffect, useRef, useState } from 'react'
import { KeyboardAvoidingView, View, Platform, TouchableOpacity, ScrollView, useWindowDimensions, Image } from 'react-native'
import { useIsFocused } from '@react-navigation/native'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import base64 from "react-native-base64";

// Common components
import CommonHeader from '../../shared/commonComponents/commonHeader'
import { myAccountStyle } from '../../themes/components/profile/myAccountStyle'
import { OrderSettingsStyle } from '../../themes/components/settings/orderSettingsStyle'
import { appColors, fontSize } from '../../themes/appTheme'
import { useAppNavigation } from '../../types/@react-navigation/navigation'
import { ZenText, ZenInput, ZenButton, ZenLoader, ZenIcon, ZenToast } from 'zen-core-elements'
import { CustomModal } from '../../shared/commonComponents/customModal'
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks'
import { changeEmail, deactivateAccount, getOnePluginDetails, getUserDetails, loginState, logOutCurrentUser, setClockOutDevice, updateUserNotificationDetails, uploadLogoUrl } from '../../redux/slices/commonSlice'
import { config } from '../../shared/environment'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { checkPasswordForAdminProfile, registerUser } from '../../redux/slices/authSlice'
import Popover from 'react-native-popover-view'
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox'
import { updatePin, verifyEmail } from '../../redux/slices/staffSlice'
import { copyoruploadPayload, copyoruploadResponse } from '../products/productList/productModule'
import { copyorupload } from '../../redux/slices/productSlice'
import CommonUpload from '../../shared/commonComponents/commonUpload'
import { ZenRBSheetRef } from 'zen-core-elements/src/elements/zenRBSheet/RBSheet'
import CommonImageUpload, { commonPickerRes } from '../../shared/commonComponents/commonImageUpload'
import { showApiError } from '../../shared/utils/handleApiError';
import { shallowEqual } from 'react-redux'

interface ProfileFormValues {
    name: string;
    contactNumber: string;
    email: string;
    newEmail: string;
    emailNotification?: boolean;
    smsNotification?: boolean;
    pushNotification?: boolean;
    isTwoStepVerification?: boolean;
    isSmsVerification?: boolean;
}

const MyAccount: FC = () => {
    // Variable used to store Focused state
    const isFocused = useIsFocused()
    const dispatch = useAppDispatch()
    const bottomRef = useRef<ZenRBSheetRef>(null)
    const { isWrite: isProfileWrite, isRead: isProfileRead } = useAppSelector(getScreenPermission("ProfileStack"), shallowEqual)

    // Variable whlich is used for navigation
    const navigation = useAppNavigation()
    // Variable which is used to render styles based on device
    const { width, height } = useWindowDimensions()
    // Variable which is used handle loader
    const [isLoading, setIsLoading] = useState<boolean>(false)
    // Variable which is to store current user details
    const currentUser = useAppSelector((state) => state.common.currentUser?.value)
    const userDetails = useAppSelector((state) => state.common.userDetails.value)
    const onePosPlugin = useAppSelector((state) => state.common?.pluginDetails.value)
    const checkPasswordValid = useAppSelector((state) => state.authSlice.checkPasswordValid)
    const verifyEmailData = useAppSelector((state) => state.staff.verifyEmail)
    const [isEditing, setIsEditing] = useState({ changePinOpen: false, changeProfileOpen: false });
    const [confirmationPopover, setConfirmationPopover] = useState<{ isVisible: boolean, dialogType?: "mailNotification" | "pushNotification" | "smsNotification" | "deactivateAccount" }>({ isVisible: false })
    console.log(checkPasswordValid, "checkPasswordValid");

    const [transparentLoader, setTransparentLoader] = useState<boolean>(false)
    // Varible which is used to open/close popover state
    const [openModal, setOpenModal] = useState<boolean>(false)
    // State Variable which is used to handle current password visibility
    const [passwordVisibility, setPasswordVisibility] = useState({
        currentPIN: { security: true, rightIcon: 'eye-off' },
        newPIN: { security: true, rightIcon: 'eye-off' },
        confirmPIN: { security: true, rightIcon: 'eye-off' },
        currentModalPassword: { security: true, rightIcon: "eye-off" }
    });
    const [isVerifyPinModalOpen, setIsVerifyPinModalOpen] = useState(false)

    // Variable which is used to define validation for form fields
    const profileValidationSchema = Yup.object().shape({
        name: Yup.string()
            .label('Name')
            .required('First name is required')
            .matches(/^[a-zA-Z\/\s\'-().\-]+(\\s[a-zA-Z\/\s\'-().]+)*$/, 'First name accepts only characters')
            .max(40, 'First name should not exceed 40 characters'),
        contactNumber: Yup.string()
            .required('Contact number is required')
            .length(10, 'Contact number should not exceed 10 digits and first digit cannot be 0 or 1')
            .matches(/\b\d{10}\b/, 'Contact number should not exceed 10 digits and first digit cannot be 0 or 1')
            .test('Length Check', 'Contact number should not exceed 10 digits and first digit cannot be 0 or 1',
                (value) => {
                    let temp
                    if (value) {
                        temp = ((parseInt(value) >= 2000000000 && parseInt(value) <= 9999999999) ? true : false)
                        return temp
                    } else {
                        return true
                    }
                }
            ),
        newEmail: Yup.string()
            .label("newEmail")
            .matches(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, "Email id is invalid")
            .test('email validation', 'Email is required', (value) => {
                if (openModal) {
                    return value ? true : false
                } else {
                    return true
                }
            })
    })

    // Variable which is used to access profileFormik
    const profileFormik = useFormik<ProfileFormValues>({
        enableReinitialize: true,
        initialValues: {
            name: userDetails?.firstName ? String(userDetails?.firstName) : '',
            contactNumber: userDetails?.phone ? userDetails?.phone.toString() : currentUser?.phone.toString() || '',
            email: userDetails?.email ? userDetails?.email : '',
            newEmail: '',
            emailNotification: userDetails?.emailNotification,
            smsNotification: userDetails?.smsNotification,
            pushNotification: userDetails?.pushNotification,
            isTwoStepVerification: userDetails?.isTwoStepVerification,
            isSmsVerification: userDetails?.isSmsVerification,
        },
        validationSchema: profileValidationSchema,
        // Close the edit card once the update succeeds
        onSubmit: (values) => updateNotificationDetails(values).then(() => setIsEditing(prev => ({ ...prev, changeProfileOpen: false }))).catch(() => { })
    })

    const currentPinValidation = Yup.object().shape({
        currentPin: Yup.string()
            .required('Current PIN is required')
            .length(4, 'Current PIN should not exceed 4 digits')
            .matches(/\b\d{4}\b/, 'Current PIN should not exceed 4 digits'),
    })

    const currentPinFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            currentPin: '',
        },
        validationSchema: currentPinValidation,
        onSubmit: () => setConfirmationPopover({ isVisible: true, dialogType: "deactivateAccount" })
    })

    const pinValidationSchema = Yup.object().shape({
        currentPIN: Yup.string()
            .required('Current PIN is required')
            .length(4, 'Current PIN should not exceed 4 digits')
            .matches(/\b\d{4}\b/, 'Current PIN should not exceed 4 digits'),
        newPIN: Yup.string()
            .required('New PIN is required')
            .length(4, 'New PIN should not exceed 4 digits')
            .matches(/\b\d{4}\b/, 'New PIN should not exceed 4 digits'),
        confirmPIN: Yup.string()
            .required('Confirm PIN is required')
            .length(4, 'Confirm PIN should not exceed 4 digits')
            .matches(/\b\d{4}\b/, 'Confirm PIN should not exceed 4 digits')
            .test('confirmPIN', 'Confirm PIN does not match', function (value) {
                if (value !== this.parent.newPIN) {
                    return this.createError({
                        message: 'Confirm PIN does not match',
                    });
                }
                return true;
            }),
    })

    const pinFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            currentPIN: '',
            newPIN: '',
            confirmPIN: '',
        },
        validationSchema: pinValidationSchema,
        onSubmit: () => updatePinApi()
    })

    // UseEffect used to make corresponding API calls while page is focused
    useEffect(() => {
        if (isFocused) {
            getStoreDetails()
        }
    }, [isFocused])

    //Function to check old password match
    useEffect(() => {
        const handler = setTimeout(() => {
            if (isVerifyPinModalOpen && currentPinFormik?.values?.currentPin) {
                (Yup.reach(currentPinValidation, 'currentPin') as Yup.StringSchema)
                    .isValid(currentPinFormik?.values?.currentPin)
                    .then((valid: boolean) => {
                        if (valid) {
                            checkPassword();
                        }
                    });
            }
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [currentPinFormik?.values?.currentPin, isVerifyPinModalOpen]);

    const checkPassword = async () => {
        await dispatch(checkPasswordForAdminProfile({
            registration: "Basic " + base64?.encode(userDetails?.email + ":" + "pin:" + currentPinFormik?.values?.currentPin)
        }))

    }

    // Method which is used to get user details
    const getStoreDetails = () => {
        dispatch(getUserDetails({ userId: currentUser?.userId!, storeId: config.storeId })).unwrap().catch(showApiError).finally(() => {
            getOnePosPluginApi()
        })
    }

    const getOnePosPluginApi = async () => {
        await dispatch(getOnePluginDetails({ storeId: config.storeId, data: { pluginCode: "SMS_NOTIFICATION" } })).unwrap().catch(showApiError).finally(() => {
            setIsLoading(false)
        })
    }

    const updateNotificationDetails = (values: ProfileFormValues) => {
        setTransparentLoader(true)
        const paylaod = {
            userId: currentUser?.userId!,
            storeId: config.storeId,
            data: {
                emailNotification: values.emailNotification!,
                smsNotification: values?.smsNotification!,
                pushNotification: values?.pushNotification!,
                isTwoStepVerification: values?.isTwoStepVerification!,
                isSmsVerification: values?.isSmsVerification!,
                firstName: values?.name!,
                phone: values?.contactNumber!
            }
        }
        return dispatch(updateUserNotificationDetails(paylaod)).unwrap().catch(showApiError).finally(() => {
            setTransparentLoader(false)
        })
    }
    const checkEmail = async () => {
        if (profileFormik?.values?.newEmail != null && profileFormik?.values?.newEmail != '') {
            const data = { email: profileFormik?.values?.newEmail }
            await dispatch(verifyEmail({ data }))
        }
    }

    //Function used to handle the image picker actions
    const handleImage = (name: string, res?: commonPickerRes[]) => {
        switch (name) {
            case 'Camera':
            case 'Library':
                bottomRef?.current?.close()
                if (res && res.length > 0) {
                    const data = {
                        fileName: res?.[0]?.fileName!,
                        fileUrl: res?.[0]?.originalPath!,
                        ext: res?.[0]?.fileName?.split(".")?.pop()!,
                        image: res?.[0]?.key!,
                        url: config?.AWSImageUrl + res?.[0]?.key!
                    }
                    uploadLogoUrlApi(data)
                }
                break;

            case 'Cancel':
                bottomRef?.current?.close()
                break;
        }
    }

    const uploadLogoUrlApi = async (data: {
        fileName: string;
        fileUrl: string;
        ext: string;
        image: string;
        url: string;
    }) => {
        const payload = {
            storeId: config.storeId,
            userId: currentUser?.userId!,
            data: {
                deletedLogoUrl: "",
                isCompressed: false,
                logoImage: [{
                    ext: data?.ext,
                    fileName: data?.fileName,
                    image: data?.image,
                    url: data?.url
                }],
                storeId: config.storeId,
                userId: currentUser?.userId!
            }
        }
        await dispatch(uploadLogoUrl(payload)).unwrap().then((response) => {
            if (response) {
                console.log(response);
                ZenToast({ type: "success", message: "Logo updated successfully" })
            }
        }).catch(showApiError).finally(() => {
            setTransparentLoader(false)
        })
    }


    useEffect(() => {
        const handler = setTimeout(() => {
            if (openModal && profileFormik.values.newEmail) {
                (Yup.reach(profileValidationSchema, 'newEmail') as Yup.StringSchema)
                    .isValid(profileFormik.values.newEmail)
                    .then((valid: boolean) => {
                        if (valid) {
                            checkEmail();
                        }
                    });
            }
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [profileFormik.values.newEmail, openModal]);


    const deactivateAccountApi = async () => {
        setTransparentLoader(true)
        await dispatch(deactivateAccount({ data: { userId: currentUser?.userId!, email: currentUser?.email!, storeId: config.storeId } })).unwrap().then(async (res) => {
            if (res?.success) {
                await dispatch(logOutCurrentUser({ data: { infoId: currentUser?.infoId } })).unwrap().then(async (res) => {
                    if (res.success) {
                        setConfirmationPopover({ isVisible: false })
                        setIsVerifyPinModalOpen(false)
                        await AsyncStorage?.removeItem("Email")
                        dispatch(setClockOutDevice(true))
                        dispatch(loginState(false))
                        dispatch({ type: 'RESET_STORE' })
                        ZenToast({ message: res?.message, type: "success" })
                    }
                }).catch((err) => {
                    console.log("error in the logOutCurrentUser", err);
                })
            }
        }).finally(() => {
            setTransparentLoader(false)
        })
    }

    const changeEmailApi = async () => {
        setTransparentLoader(true)
        let email = await AsyncStorage?.getItem("Email")
        const data = {
            clientId: currentUser?.clientId!,
            userId: currentUser?.userId!,
            storeId: config.storeId,
            oldEmail: userDetails?.email!,
            newEmail: profileFormik?.values.newEmail,
            oldNumber: null,
            newNumber: null
        }

        await dispatch(changeEmail({ data, storeId: config.storeId, userId: currentUser?.userId! })).unwrap().then((response) => {
            if (response?.success) {
                ZenToast({
                    type: "success",
                    message: "Email updated successfully"
                })
            }
        }).catch(showApiError).finally(() => {
            setTransparentLoader(false)
        })
    }
    const updatePinApi = async () => {
        setTransparentLoader(true)
        const email = await AsyncStorage?.getItem("Email")
        const registration = "Basic " + base64?.encode(userDetails?.email + ":" + "pin:" + pinFormik?.values?.currentPIN)
        const pin = "Basic " + base64?.encode(userDetails?.email + ":" + "pin:" + pinFormik?.values.confirmPIN)

        await dispatch(updatePin({ pin, registration })).unwrap().then((response) => {
            if (response?.success) {
                ZenToast({
                    type: "success",
                    message: "Pin updated successfully"
                })
                navigation?.goBack()
            }
            else {
                ZenToast({
                    type: "error",
                    message: "Failed to update the pin, Please try again after some time"
                })
            }
        }).catch((error) => {
            if (error?.data?.statusCode == "update-Pin-failed-001") {
                ZenToast({ type: "error", message: "Invalid current pin" })
            }
            else {
                ZenToast({
                    type: "error",
                    message: "Failed to update the pin, Please try again after some time"
                })
            }
            console?.log("Pin update error", error)
        }).finally(() => {
            setTransparentLoader(false)
        })
    }



    //Function which is used to handle new password visibility
    const handlePasswordVisibility = (type: "currentPIN" | "newPIN" | "confirmPIN" | "currentModalPassword") => {
        setPasswordVisibility(prevState => ({
            ...prevState,
            [type]: {
                rightIcon: prevState[type].rightIcon === 'eye' ? 'eye-off' : 'eye',
                security: !prevState[type].security
            }
        }))
    }

    //variable to store the mobile notification confirmation dialog
    const mobileNotificationDialogProps = {
        content: 'Are you sure you want to turn ON/OFF Mobile Notification?',
        button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
        icon: {
            name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community'
        }
    }
    //variable to store the email notification confirmation dialog
    const emailNotificationDialogProps = {
        content: "Are you sure you want to turn ON/OFF Email Notification?",
        button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
        icon: {
            name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community'
        }
    }
    //variable to store the sms notification confirmation dialog
    const smsNotificationDialogProps = {
        content: "Are you sure you want to turn ON/OFF SMS Notification?",
        button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
        icon: {
            name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community'
        }
    }

    //variable to store the sms notification confirmation dialog
    const deactivateAccountDialogProps = {
        content: "Are you sure you want to deactivate this store?",
        button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
        icon: {
            name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community'
        }
    }

    const handleConfirmation = (item: { id: number }) => {
        if (item?.id === 1) {
            const dialogType = confirmationPopover?.dialogType;
            const isSmsPluginActive = onePosPlugin?.[0]?.storePluginMappings?.[0]?.isActive;

            const updatedValues = { ...profileFormik.values };

            if (dialogType === "smsNotification" && !isSmsPluginActive) {
                ZenToast({
                    message: "Please enable SMS Notification plugin for mobile notification",
                    type: "warning",
                });
            } else {
                if (dialogType === "mailNotification") {
                    updatedValues.emailNotification = !updatedValues.emailNotification;
                } else if (dialogType === "pushNotification") {
                    updatedValues.pushNotification = !updatedValues.pushNotification;
                } else if (dialogType === "smsNotification") {
                    updatedValues.smsNotification = !updatedValues.smsNotification;
                } else if (dialogType === "deactivateAccount") {
                    return deactivateAccountApi()
                }

                profileFormik.setValues(updatedValues);
                updateNotificationDetails(updatedValues);
            }
        }

        // Always close popover (for id 1 & 2)
        setConfirmationPopover({ isVisible: false });
    };

    // ----------------------------------------------------
    // Layout helpers
    // ----------------------------------------------------
    const isLandscape = width > height
    const roleName = userDetails?.role?.role || currentUser?.roleCode
    const phoneNumber = userDetails?.phone || currentUser?.phone
    const phoneLabel = phoneNumber ? `+${userDetails?.countryCode || currentUser?.countryCode} ${phoneNumber}` : ''

    const cancelProfileEdit = () => {
        profileFormik.resetForm()
        setIsEditing(prev => ({ ...prev, changeProfileOpen: false }))
    }

    const cancelPinEdit = () => {
        pinFormik.resetForm()
        setIsEditing(prev => ({ ...prev, changePinOpen: false }))
    }

    const renderInfoRow = (icon: string, label: string, value?: string | null) => (
        <View style={myAccountStyle.infoRow}>
            <View style={myAccountStyle.infoIcon}>
                <ZenIcon type='material-community' name={icon} size={18} color={appColors.primary} />
            </View>
            <View style={{ flex: 1 }}>
                <ZenText content={label} style={myAccountStyle.infoLabel} />
                <ZenText content={value || '-'} style={myAccountStyle.infoValue} />
            </View>
        </View>
    )

    const renderDetail = (label: string, value?: string | null) => (
        <View style={myAccountStyle.fieldCol}>
            <ZenText content={label} style={myAccountStyle.infoLabel} />
            <ZenText content={value || '-'} style={myAccountStyle.detailValue} />
        </View>
    )

    const renderCardActions = ({ isOpen, editTitle, saveTitle, onEdit, onCancel, onSave }: { isOpen: boolean, editTitle: string, saveTitle: string, onEdit: () => void, onCancel: () => void, onSave: () => void }) => (
        isOpen ? (
            <View style={OrderSettingsStyle.headerButtonsRow}>
                <ZenButton title='Cancel' size='sm' titleColor={appColors.lightDark} buttonStyle={OrderSettingsStyle.backButton} titleStyle={OrderSettingsStyle.buttonTitleStyle} onPress={onCancel} />
                <ZenButton title={saveTitle} size='sm' titleColor={appColors.light} buttonStyle={OrderSettingsStyle.updateButton} titleStyle={OrderSettingsStyle.buttonTitleStyle} onPress={onSave} />
            </View>
        ) : (
            <ZenButton title={editTitle} size='sm' titleColor={appColors.primary} buttonStyle={myAccountStyle.outlineButton} titleStyle={[OrderSettingsStyle.buttonTitleStyle, { color: appColors.primary }]} onPress={onEdit} />
        )
    )

    const inputProps = {
        type: 'outlined' as const,
        labelStyle: myAccountStyle.labelStyle,
        inputStyle: myAccountStyle.inputStyle,
        inputContainerStyle: myAccountStyle.inputContainerStyle,
        containerStyle: { paddingHorizontal: 0 },
        errorStyle: { color: appColors.red },
    }

    return (
        <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} style={myAccountStyle.container}>
            <CommonHeader headerName='My Account' backIcon={true} backIconOnpress={() => { navigation.goBack() }} />
            {isLoading ? <View style={myAccountStyle.loader}>
                <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
            </View> :
                <ScrollView showsVerticalScrollIndicator={false} style={myAccountStyle.innerContainer} contentContainerStyle={myAccountStyle.scrollContent}>
                    <View style={[myAccountStyle.columns, { flexDirection: isLandscape ? 'row' : 'column' }]}>

                        {/* ==================================================== */}
                        {/* PROFILE SUMMARY                                      */}
                        {/* ==================================================== */}
                        <View style={isLandscape ? myAccountStyle.summaryColumn : undefined}>
                            <View style={[OrderSettingsStyle.card, myAccountStyle.summaryCard]}>
                                <View style={myAccountStyle.avatarWrapper}>
                                    <Image source={userDetails?.profileImage ? { uri: config.AWSImageUrl + userDetails?.profileImage } : require('../../assets/profile.png')} style={myAccountStyle.avatar} />
                                    <TouchableOpacity activeOpacity={0.7} style={myAccountStyle.cameraView} onPress={() => bottomRef?.current?.open()}>
                                        <ZenIcon type='feather' name="camera" size={16} color={appColors?.light} />
                                    </TouchableOpacity>
                                </View>
                                <ZenText content={userDetails?.firstName || '-'} style={myAccountStyle.profileName} />
                                {roleName ? (
                                    <View style={myAccountStyle.roleBadge}>
                                        <ZenText content={roleName} style={myAccountStyle.roleBadgeText} />
                                    </View>
                                ) : null}

                                <View style={myAccountStyle.summaryDivider} />

                                {renderInfoRow('email-outline', 'Email', userDetails?.email)}
                                {renderInfoRow('phone-outline', 'Phone', phoneLabel)}

                                <ZenText content='Tap the camera icon to change your profile photo.' style={[OrderSettingsStyle.helperText, { textAlign: 'center' }]} />
                            </View>
                        </View>

                        <View style={isLandscape ? myAccountStyle.detailsColumn : undefined}>
                            {/* ==================================================== */}
                            {/* PROFILE DETAILS                                      */}
                            {/* ==================================================== */}
                            <View style={OrderSettingsStyle.card}>
                                <View style={OrderSettingsStyle.cardHeaderRow}>
                                    <View style={OrderSettingsStyle.headerTitleView}>
                                        <ZenText content='Profile Details' style={OrderSettingsStyle.cardTitle} />
                                        <ZenText content='Your profile is used to sign in to the store. Email cannot be changed here.' style={OrderSettingsStyle.cardSubtitle} />
                                    </View>
                                    {renderCardActions({
                                        isOpen: isEditing.changeProfileOpen,
                                        editTitle: 'Edit',
                                        saveTitle: 'Save',
                                        onEdit: () => setIsEditing(prev => ({ ...prev, changeProfileOpen: true })),
                                        onCancel: cancelProfileEdit,
                                        onSave: () => profileFormik.handleSubmit(),
                                    })}
                                </View>

                                {isEditing.changeProfileOpen ? (
                                    <View style={myAccountStyle.fieldGrid}>
                                        <View style={myAccountStyle.fieldCol}>
                                            <ZenInput
                                                {...inputProps}
                                                label='Name *'
                                                onChangeText={(text: string) => profileFormik.setFieldValue('name', text)}
                                                value={profileFormik?.values?.name}
                                                onBlur={() => {
                                                    profileFormik.setFieldTouched('name', true);
                                                    profileFormik.setFieldValue('name', profileFormik?.values?.name.trim())
                                                }}
                                                errorMessage={profileFormik?.touched?.name && profileFormik?.errors?.name ? profileFormik?.errors?.name : undefined}
                                            />
                                        </View>
                                        <View style={myAccountStyle.fieldCol}>
                                            <ZenInput
                                                {...inputProps}
                                                label='Contact Number *'
                                                keyboardType='phone-pad'
                                                maxLength={10}
                                                onChangeText={(text: string) => profileFormik.setFieldValue('contactNumber', text)}
                                                value={profileFormik?.values?.contactNumber}
                                                onBlur={() => {
                                                    profileFormik.setFieldTouched('contactNumber', true);
                                                    profileFormik.setFieldValue('contactNumber', profileFormik?.values?.contactNumber.trim())
                                                }}
                                                errorMessage={profileFormik?.touched?.contactNumber && profileFormik?.errors?.contactNumber ? profileFormik?.errors?.contactNumber : undefined}
                                            />
                                        </View>
                                        <View style={myAccountStyle.fieldCol}>
                                            <ZenInput
                                                {...inputProps}
                                                label='Email Id'
                                                editable={false}
                                                disabled={true}
                                                keyboardType='email-address'
                                                value={profileFormik?.values?.email}
                                            />
                                        </View>
                                    </View>
                                ) : (
                                    <View style={myAccountStyle.fieldGrid}>
                                        {renderDetail('Name', userDetails?.firstName)}
                                        {renderDetail('Contact Number', phoneLabel)}
                                        {renderDetail('Email Id', userDetails?.email)}
                                    </View>
                                )}
                            </View>

                            {/* ==================================================== */}
                            {/* SECURITY                                             */}
                            {/* ==================================================== */}
                            <View style={OrderSettingsStyle.card}>
                                <View style={OrderSettingsStyle.cardHeaderRow}>
                                    <View style={OrderSettingsStyle.headerTitleView}>
                                        <ZenText content='Security' style={OrderSettingsStyle.cardTitle} />
                                        <ZenText content='Your 4-digit PIN is used to sign in to the POS.' style={OrderSettingsStyle.cardSubtitle} />
                                    </View>
                                    {renderCardActions({
                                        isOpen: isEditing.changePinOpen,
                                        editTitle: 'Change PIN',
                                        saveTitle: 'Update PIN',
                                        onEdit: () => setIsEditing(prev => ({ ...prev, changePinOpen: true })),
                                        onCancel: cancelPinEdit,
                                        onSave: () => pinFormik.handleSubmit(),
                                    })}
                                </View>

                                {isEditing.changePinOpen ? (
                                    <View style={myAccountStyle.fieldGrid}>
                                        {([
                                            { key: 'currentPIN', label: 'Current PIN' },
                                            { key: 'newPIN', label: 'New PIN' },
                                            { key: 'confirmPIN', label: 'Confirm PIN' },
                                        ] as const).map(({ key, label }) => (
                                            <View style={myAccountStyle.fieldCol} key={key}>
                                                <ZenInput
                                                    {...inputProps}
                                                    label={label}
                                                    maxLength={4}
                                                    keyboardType='numeric'
                                                    secureTextEntry={passwordVisibility[key].security}
                                                    onChangeText={(text: string) => pinFormik.setFieldValue(key, text)}
                                                    value={pinFormik?.values?.[key]}
                                                    rightIcon={<ZenIcon type='ionicon' onPress={() => handlePasswordVisibility(key)} name={passwordVisibility[key].rightIcon} size={20} color={appColors.grey} />}
                                                    onBlur={() => {
                                                        pinFormik.setFieldTouched(key, true);
                                                        pinFormik.setFieldValue(key, pinFormik?.values?.[key].trim())
                                                    }}
                                                    errorMessage={pinFormik?.errors?.[key] && pinFormik?.touched?.[key] ? pinFormik?.errors?.[key] : undefined}
                                                />
                                            </View>
                                        ))}
                                    </View>
                                ) : (
                                    <View style={myAccountStyle.pinRow}>
                                        <View style={myAccountStyle.infoIcon}>
                                            <ZenIcon type='material-community' name='lock-outline' size={18} color={appColors.primary} />
                                        </View>
                                        <View>
                                            <ZenText content='PIN' style={myAccountStyle.infoLabel} />
                                            <ZenText content='• • • •' style={myAccountStyle.pinMask} />
                                        </View>
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                </ScrollView>}
            <CustomModal transparent={true} visible={openModal} style={{ backfaceVisibility: 'visible' }}>
                <View style={myAccountStyle.container}>
                    <View style={[myAccountStyle.headerView, { backgroundColor: '#rgba(0,0,0,0.4)' }]}>
                        <View style={myAccountStyle.headerView}>
                            <View style={[myAccountStyle.headerStyle, { width: width > 600 ? 500 : 350 }]}>
                                <ZenText content='Update Email' style={{ color: appColors.primary, textAlign: 'left', marginLeft: 3 }} size={fontSize?.mediumFontSize} bold={true} />
                                <ZenIcon name='close-circle' type='material-community' color={appColors.lightDark} size={20} style={{ marginTop: 5 }}
                                    onPress={() => { setOpenModal(false) }}
                                />
                            </View>
                            <View style={{ width: width > 600 ? 500 : 350 }}>
                                <View style={{ backgroundColor: appColors?.light, paddingVertical: 10, paddingHorizontal: 15 }}>
                                    <ZenInput
                                        type='outlined'
                                        label='New Email Id *'
                                        keyboardType='email-address'
                                        onChangeText={(text: string) => profileFormik.setFieldValue('newEmail', text)}
                                        value={profileFormik?.values?.newEmail}
                                        labelStyle={myAccountStyle.labelStyle}
                                        inputStyle={myAccountStyle.inputStyle}
                                        inputContainerStyle={myAccountStyle.inputContainerStyle}
                                        rightIcon={verifyEmailData?.loading ? <ZenLoader size={"small"} /> : undefined}
                                        onBlur={() => {
                                            profileFormik.setFieldTouched('newEmail', true);
                                            profileFormik.setFieldValue('newEmail', profileFormik?.values?.newEmail.trim())
                                        }}
                                        errorMessage={(profileFormik?.touched?.newEmail && profileFormik?.errors?.newEmail) ? profileFormik?.errors?.newEmail : (verifyEmailData?.value?.emailExists ? 'Email already exists!' : "")}
                                        errorStyle={{ color: appColors.red, }}
                                    />
                                </View>
                                <View style={myAccountStyle.updateBtnView}>
                                    <ZenButton
                                        title={'Cancel'}
                                        onPress={() => setOpenModal(false)}
                                        buttonStyle={[myAccountStyle.buttonStyle, { backgroundColor: appColors?.light }]}
                                        titleStyle={[myAccountStyle.titleStyle, { color: appColors.lightDark }]} />
                                    <ZenButton title={'Update'} onPress={() => changeEmailApi()} buttonStyle={[myAccountStyle.updateButtonStyle, { backgroundColor: appColors?.primary }]} titleStyle={[myAccountStyle.titleStyle, { color: appColors.light }]} />
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            </CustomModal>
            <Popover isVisible={confirmationPopover?.isVisible} popoverStyle={{ width: width > 900 ? 500 : 450, borderRadius: 10 }}>
                <CommonDialogBox type='OTHER' dialog={confirmationPopover?.dialogType === "mailNotification" ? emailNotificationDialogProps : confirmationPopover?.dialogType === "pushNotification" ? mobileNotificationDialogProps : confirmationPopover?.dialogType === "deactivateAccount" ? deactivateAccountDialogProps : smsNotificationDialogProps} confirmation={handleConfirmation} />
            </Popover>
            <CustomModal visible={transparentLoader} transparent={true}>
                <ZenLoader color={appColors?.light} lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
            </CustomModal>

            <CustomModal transparent={true} visible={isVerifyPinModalOpen} style={{ backfaceVisibility: 'visible' }}>
                <View style={myAccountStyle.container}>
                    <View style={[myAccountStyle.headerView, { backgroundColor: '#rgba(0,0,0,0.4)' }]}>
                        <View style={myAccountStyle.headerView}>
                            <View style={[myAccountStyle.headerStyle, { width: width > 600 ? 500 : 350 }]}>
                                <ZenText content='Verify Your PIN' style={{ color: appColors.primary, textAlign: 'left', marginLeft: 3 }} size={fontSize?.mediumFontSize} bold={true} />
                                <ZenIcon name='close-circle' type='material-community' color={appColors.lightDark} size={20} style={{ marginTop: 5 }}
                                    onPress={() => { setIsVerifyPinModalOpen(false) }}
                                />
                            </View>
                            <View style={{ width: width > 600 ? 500 : 350 }}>
                                <View style={{ backgroundColor: appColors?.light, paddingVertical: 10, paddingHorizontal: 15 }}>
                                    <ZenInput
                                        type='outlined'
                                        label='Current PIN'
                                        maxLength={4}
                                        keyboardType='numeric'
                                        secureTextEntry={passwordVisibility.currentModalPassword.security}
                                        onChangeText={(text: string) => currentPinFormik.setFieldValue('currentPin', text)}
                                        value={currentPinFormik?.values?.currentPin}
                                        labelStyle={[myAccountStyle.labelStyle, { marginHorizontal: 10 }]}
                                        inputStyle={myAccountStyle.inputStyle}
                                        inputContainerStyle={[myAccountStyle.inputContainerStyle, { marginHorizontal: 10 }]}
                                        rightIcon={checkPasswordValid?.loading ? <ZenLoader size={"small"} color={appColors.primary} /> : <ZenIcon type='ionicon' onPress={() => handlePasswordVisibility('currentModalPassword')} name={passwordVisibility.currentModalPassword.rightIcon} size={20} color={appColors.grey} />}
                                        onBlur={() => {
                                            currentPinFormik.setFieldTouched('currentPin', true);
                                            currentPinFormik.setFieldValue('currentPin', currentPinFormik?.values?.currentPin.trim())
                                        }}
                                        errorMessage={currentPinFormik.touched.currentPin && currentPinFormik.errors.currentPin ? currentPinFormik.errors.currentPin : checkPasswordValid?.value?.passwordExists === false ? "Current PIN mismatch" : ""}
                                        errorStyle={{ color: appColors.red, paddingLeft: 10, marginBottom: currentPinFormik?.errors?.currentPin && currentPinFormik?.touched?.currentPin ? 10 : 0 }}
                                    />
                                </View>
                                <View style={myAccountStyle.updateBtnView}>
                                    <ZenButton
                                        title={'Cancel'}
                                        onPress={() => { setIsVerifyPinModalOpen(false) }}
                                        buttonStyle={[myAccountStyle.buttonStyle, { backgroundColor: appColors?.light }]}
                                        titleStyle={[myAccountStyle.titleStyle, { color: appColors.lightDark }]} />
                                    <ZenButton title={'Deactivate'} onPress={() => currentPinFormik.handleSubmit()} buttonStyle={[myAccountStyle.updateButtonStyle, { backgroundColor: appColors?.primary }]} titleStyle={[myAccountStyle.titleStyle, { color: appColors.light }]} />
                                </View>
                            </View>
                        </View>
                    </View>
                </View>
            </CustomModal>
            <CommonImageUpload
                bottomRef={bottomRef}
                propFn={handleImage}
                gallery={{ isStoreGallery: false }}
                selectedIndex={{ value: { imagesMaxLength: 1 } }}
            />
        </KeyboardAvoidingView >
    )
}

export default MyAccount;
